import type { Insets } from "./config";

/**
 * Keeps a puzzle's artwork inside its frame.
 *
 * The payload puzzles were authored against a full-height screen and several of
 * them size their artwork as `width: 100%; height: auto` with no height cap —
 * p10's apparatus diagram and p6's photograph of the box both do. Boxed into the
 * preview's content area (which stops above the answer bar) the image is then far
 * taller than the frame, so its bottom third is simply not on screen: p10's
 * SAMPLE beaker and the lower half of p6's box disappear behind the desk, and the
 * puzzle becomes unsolvable. The ones that do look right (p3, p5) only work by
 * accident, via quirks-mode percentage height inheritance.
 *
 * Rather than editing five payload files — which would change the show itself —
 * the preview injects a stylesheet into the iframe. The frames are same-origin
 * (served from /api/load_puzzle), so this is just a stylesheet append. It pins
 * the artwork's container to the frame and letterboxes the image inside it with
 * `object-fit: contain`, which is exactly the case the eyeglass magnifier already
 * corrects for (see `paintedRect` in public/blowup/blowup.min.js), so the lens
 * keeps tracking the picture.
 *
 * `reserve` keeps a strip clear for controls the puzzle paints in a fixed corner,
 * so they never sit on top of the artwork.
 */

const STYLE_ID = "preview-fit";

function css(reserve: Insets): string {
  const { top = 0, right = 0, bottom = 0, left = 0 } = reserve;
  return `
    html, body {
      height: 100% !important;
      max-height: 100% !important;
      margin: 0 !important;
      overflow: hidden !important;
    }

    /*
     * Positioned so the artwork box below can be absolute against it. It must be
     * absolute and NOT fixed: in Chrome a fixed element always starts a new
     * stacking context, which would trap the z-index of anything the puzzle
     * paints inside that box. p6 puts its combination dials there at z-index
     * 9999 to sit above its own #fade vignette (z-index 10) — boxed into a
     * stacking context, the dials lose and the vignette casts a shadow over
     * them. Absolute positioning leaves the puzzle's own layering intact.
     */
    body { position: relative !important; }

    /* The box the artwork is allowed to occupy, inset by any reserved strip. */
    .img-magnifier-container, #box {
      position: absolute !important;
      top: ${top}px !important;
      right: ${right}px !important;
      bottom: ${bottom}px !important;
      left: ${left}px !important;
      width: auto !important;
      height: auto !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    /* Letterbox the artwork inside that box, whatever the payload asked for. */
    .img-magnifier-container > img, #box > img {
      position: absolute !important;
      top: 0 !important;
      left: 0 !important;
      width: 100% !important;
      height: 100% !important;
      max-width: none !important;
      max-height: none !important;
      transform: none !important;
      object-fit: contain !important;
      object-position: center center !important;
    }

    /* Same treatment for a puzzle whose artwork sits straight on the body. */
    body > img {
      position: absolute !important;
      top: ${top}px !important;
      right: ${right}px !important;
      bottom: ${bottom}px !important;
      left: ${left}px !important;
      width: auto !important;
      height: auto !important;
      max-width: none !important;
      max-height: none !important;
      transform: none !important;
      object-fit: contain !important;
      object-position: center center !important;
    }
  `;
}

/**
 * Inject (or refresh) the fitting stylesheet in a loaded puzzle iframe. Safe to
 * call repeatedly. Does nothing if the document isn't reachable — a puzzle that
 * somehow loads cross-origin keeps its own layout rather than throwing.
 */
export function fitPuzzleFrame(frame: HTMLIFrameElement | null, reserve: Insets = {}): void {
  let doc: Document | null = null;
  try {
    doc = frame?.contentDocument ?? null;
  } catch {
    return; // cross-origin; nothing we can do from here
  }
  if (!doc?.head) return;

  const existing = doc.getElementById(STYLE_ID);
  if (existing) existing.remove();

  const style = doc.createElement("style");
  style.id = STYLE_ID;
  style.textContent = css(reserve);
  doc.head.appendChild(style);
}
